import {imageMap} from "../../../../images/imageMap.ts";
import RelatedArticlesComponent from "../../../../components/help/RelatedArticlesComponent.tsx";
import QuestionAnswerComponent, {Question} from "../../../../components/help/QuestionAnswerComponent.tsx";
import Icon from "../../../../components/selfcare/Icon.tsx";
import {Link} from "react-router-dom";
import Routing from "../../../../Routing.ts";
import IconOpenInNew from "../../../../components/icons/IconOpenInNew.tsx";
import OrderAddressSearchComponent from "../../../../components/order/OrderAddressSearchComponent.tsx";

const external_factors: Question[] = [
    {
        question: "Du tester i en browser",
        answer:
            <ul className="list list--bullet">
                <li>
                    <p>
                        I en browser kan din hastighed være begrænset af, hvor hurtigt webstedet sender data.
                    </p>
                </li>
                <li>
                    <p>
                        Browser-extensions kan også påvirke dit testresultat negativt.
                        Er du nysgerrig på, hvad dette kunne være, så kan du læse mere nede i afsnittet &ldquo;VPN eller antivirus-software på din computer&rdquo;.
                    </p>
                </li>
                <li>
                    <p>
                        Sørg for at downloade en app til hastighedstesten i stedet for at teste i din browser.
                    </p>
                </li>
            </ul>
    },
    {
        question: "Testserveren, du tester op imod, kan være belastet",
        answer:
            <p>
                Når du laver en hastighedstest, kan du betragte det som, at du sender data mod en offentligt tilgængelig testserver.
                Er der 50 andre, der gør det samtidig, får du et dårligere resultat, end hvis 5 andre testede samtidig.
            </p>
    },
    {
        question: "VPN eller antivirus-software på din computer",
        answer:
            <ul className="list list--bullet">
                <li>
                    <p>
                        Sørg for at deaktivere en eventuel VPN.
                    </p>
                </li>
                <li>
                    <p>
                        Proxyservere og kryptering kan have en negativ effekt på din hastighedstest.
                        De tilføjer nemlig et ekstra lag, som din data skal rejse igennem.
                    </p>
                </li>
            </ul>
    },
    {
        question: "Dit eget netværkskort",
        answer:
            <p>
                Måske har du glemt at opdatere drivere, måske er netværkskortet begrænset til 100 Mbit/s eller ikke i stand til at trække 1000 Mbit/s.
            </p>
    }
]

const faq_before_test: Question[] = [
    {
        question: "Dine enheder skal understøtte 1000 Mbit/s",
        answer: <>
            <p>
                Ikke alle enheder understøtter 1000 Mbit/s &ndash; ældre enheder kan ikke trække lige så mange Mbit som nye.
                For at trække 1000 Mbit skal du bruge minimum CAT5e-kabling mellem vores router og dit udstyr.
            </p>
            <p>
                Bruger du switches eller hubs, skal de være Gigabit Ethernet-kompatible.
                Den interne elektronik (f.eks. USB3 hub) i flere PC&rsquo;er med indbygget Gigabit Ethernet-port understøtter nemlig ikke fuld hastighed på Ethernet-opkoblingen.
            </p>
        </>,
    },
    {
        question: "Genstart din router inden hastighedstesten",
        answer:
            <p>Når du genstarter din router, kan du rydde midlertidige problemer, og du sikrer, at den fungerer optimalt under hastighedstesten.</p>
    },
    {
        question: "Luk programmer og apps inden hastighedstesten",
        answer:
            <p>Det er en god ide at lukke programmer og apps på din enhed, så hastighedstesten ikke bliver forstyrret af dataoverførsler fra dem.</p>
    },
    {
        question: "Sæt et netværkskabel fra din router til din computer",
        answer: <p>
            Lav testen på en computer forbundet direkte til din router med et netværkskabel.
            Du kan også teste på wifi, men dit resultat vil være betydeligt anderledes.
            Du kan læse mere om, hvad du kan <Link to="/hjaelp/internet-og-wifi/forventninger-til-wifi">forvente af dit wifi her</Link>.
        </p>
    }
]

const faq_test: Question[] = [
    {
        question: "Hvad betyder download og upload?",
        answer: <>
            <p>
                Din hastighed består af to hastigheder; en download- og en uploadhastighed (f.eks. 1000/1000).
                Download-hastigheden står først, og efter skråstregen står upload-hastigheden.
            </p>
            <p>
                Download er data, du modtager fra internettet til din enhed, f.eks. når du henter filer eller streamer.
            </p>
            <p>
                Upload er data, din enhed sender via internettet, f.eks. når du uploader billeder til sociale platforme, sender en mail med vedhæftede filer eller overfører filer til en Cloud-tjeneste.
            </p>
            <p>
                Hvis du vil læse mere om download/upload på de forskellige forbindelser, kan du læse artiklen om{' '}
                <Link to={'/hjaelp/internet-og-wifi/hvilket-internet-skal-jeg-vaelge'}>
                    hvilket internet man bør vælge
                </Link>.
            </p>
        </>,
    },
    {
        question: "Hastighedstesten måler din ping – hvad betyder ping?",
        answer: <>
            <p>
                Ping måler responstid eller latenstid i dit netværk og angiver, hvor lang tid det tager for data at rejse fra din enhed til en server på internettet og tilbage igen.
            </p>
            <p>
                Ping måles typisk i millisekunder (ms) og vurderes ofte som høj eller lav.
                Det er bedst at have en lav pingtid på under 20-30 ms &ndash; så er din forbindelse hurtig, og responstiden kort.
            </p>
            <p>
                Din pingtid afhænger af, om du har internet via <Link to={Routing.Broadband.Fiber}>fibernet</Link>, <Link to={Routing.Broadband.Coax}>kabel-tv-stikket</Link> eller telefonstikket.
                På fibernet og en telefonstik-forbindelse er pingtiden typisk 5-30 ms.
                På en kabel-tv-forbindelse er den ofte 10-30 ms., og på en 5G-forbindelse kan ping svinge fra 30-70.
                Der er flere faktorer, som kan påvirke din ping-hastighed, især på <Link to={Routing.Broadband.TDC5G}>5G-internettet</Link> hvor afstanden fra router til mast samt bygnings- og vejrforhold har indflydelse.

            </p>
        </>
    },
    {
        question: "Hvad er jitter?",
        answer: <>
            <p>
                Jitter refererer til variationen i forsinkelse (latenstid) af dataoverførsler i et netværk, hvilket kan betegnes som digitalt støj.
                Jitter forekommer, hvis der er en uregelmæssighed i tidsintervallet mellem datapakker, når de sendes fra os og modtages på din router over en netværksforbindelse.
                Typisk måles jitter i millisekunder (ms).
            </p>
            <p>
                Jitter kan skyldes forskellige faktorer, herunder netværksbelastning, dårlig infrastruktur eller forældede kabler.
                For at minimere jitter kan du investere i en <Link to={Routing.Broadband.Index}>hurtigere bredbåndsforbindelse</Link> eller tjekke, om du har skabt det bedste miljø for dit internet.{' '}
                <Link to="/hjaelp/internet-og-wifi/placering-af-router">Placeringen af din router</Link> er f.eks. afgørende for dit wifi-signal.
                Du kan også opgradere til <Link to={Routing.Broadband.Fiber}>fibernet</Link>, hvor der er meget begrænset jitter.
            </p>
        </>
    },
    {
        question: "Hvorfor måler jeg en lavere internet-hastighed, end jeg betaler for?",
        answer: <>
            <p>
                Først og fremmest, skal du dobbelttjekke, at du har udført alle de ovenstående gode råd før, du laver en hastighedstest &ndash; ellers vil du få et upræcist resultat.
            </p>
            <p>
                Jo flere enheder, der bruger dit internet samtidig, desto langsommere bliver din internet-hastighed.
                Alle dine aktive enheder deler nemlig din hastighed.
            </p>
            <p>
                Hvis hastighedstesten viser et resultat, som varierer meget fra din betalte hastighed, kan du kontakte vores kundeservice, så kan vi tage et kig på dit internet sammen.
            </p>
        </>
    },
    {
        question: "Kan jeg få 1000 Mbit internet?",
        answer: <>
            <p>
                Du kan få 1000 Mbit, hvis dine enheder understøtter 1000 Mbit, og du har <Link to={Routing.Broadband.Coax}>internet via kabel-tv-stikket</Link> eller <Link to={Routing.Broadband.Fiber}>fibernettet</Link>.
                Med <Link to={Routing.Broadband.TDC5G}>5G-internet</Link>, kommer du typisk op på 950 Mbit.
            </p>
            <p>
                Du kan læse mere om, <Link to="/hjaelp/internet-og-wifi/hvordan-traekker-jeg-1000-mbit">hvordan du kan trække 1000 Mbit internet her</Link>.
                Er du allerede kunde hos os, kan du <Link to={Routing.Contact.Index}>kontakte os</Link> &ndash; så kan vi tjekke, om du kan opgradere til 1000 Mbit.
            </p>
        </>
    }
]

export default function SpeedTestArticle() {
    return (
        <>
            <article className="help-article-content">
                <div className="article">
                    <h2>Hvor hurtigt er mit internet?</h2>
                    <div className="paragraph">
                        <p>
                            Det er komplekst at teste din internethastighed, og en hastighedstest kan ikke give et endegyldigt svar på, om du får den hastighed, du betaler for.
                            En hastighedstest kan kun give et øjebliksbillede af din hastighed &ndash; du kan nemlig ikke måle din præcise hastighed.
                            Hvad enten du har fibernet, 5G eller coax internet, så er proceduren for at hastighedsteste dit internet den samme.
                        </p>
                    </div>
                </div>

                <div className="article">
                    <h2>Se hvilke eksterne faktorer, der kan påvirke resultatet af din hastighedstest</h2>
                    <QuestionAnswerComponent questions={external_factors}/>
                </div>

                <div className="article">
                    <h2>Hvordan tester jeg min hastighed?</h2>
                    <p>
                        Ønsker du at teste din hastighed, anbefaler vi, at du laver en hastighedstest med <a
                            href={'https://www.speedtest.net/'}
                            target={'_blank'}
                        >Speedtest</a>&rsquo;s app.
                        Test gerne på forskellige tidspunkter af døgnet.
                    </p>
                    <p>
                        Lav hastighedstesten på en computer, der er tilsluttet din router med et netværkskabel.
                        Vælg dit styresystem (Windows eller Mac) og download appen herunder.
                    </p>
                    <div className={'button-container'}>
                        <a
                            className={'button button--montserrat with-icon teal'}
                            href={'https://www.speedtest.net/apps/windows'}
                            target={'_blank'}
                            rel={'nofollow noreferrer'}
                        >
                            <span className={'text'}>Windows</span>
                            <Icon component={IconOpenInNew}/>
                        </a>
                        <a
                            className={'button button--montserrat with-icon teal'}
                            href={'https://www.speedtest.net/apps/mac'}
                            target={'_blank'}
                            rel={'nofollow noreferrer'}
                        >
                            <span className={'text'}>Mac</span>
                            <Icon component={IconOpenInNew}/>
                        </a>
                    </div>
                    <img
                        className="paragraph paragraph-image"
                        src={imageMap.help.speedtest.pc_illustration}
                        alt="Tilslut din computer og router med et netværkskabel for mest præcise resultater i din internet hastighedstest"
                    />
                </div>

                <div className="article">
                    <h2>Godt at vide inden, du tester din internet-hastighed</h2>
                    <QuestionAnswerComponent questions={faq_before_test}/>
                </div>

                <div className="article">
                    <h2>Godt at vide om hastighedstesten</h2>
                    <img
                        className="paragraph-image"
                        src={imageMap.help.equipment.lan_router_to_pc}
                        alt="Hastighedstesten måler upload, download og ping til at vurdere hvor hurtigt dit internet er"
                    />
                    <QuestionAnswerComponent questions={faq_test}/>
                </div>

                <div className="article">
                    <div className="paragraph">
                        <h2 className={'align-center'}>Vil du gerne have 1000 Mbit internet hos os?</h2>
                        <p className={'align-center'}>
                            Tjek din adresse og se, hvad vi kan levere til dig.
                        </p>
                        <OrderAddressSearchComponent/>
                    </div>
                </div>
            </article>

            <RelatedArticlesComponent
                articles={[
                    {
                        "category": "internet-og-wifi",
                        "article": "hvordan-traekker-jeg-1000-mbit",
                        "name": "Hvordan trækker jeg 1.000 Mbit?"
                    },
                    {
                        "category": "internet-og-wifi",
                        "article": "forventninger-til-wifi",
                        "name": "Hvad kan jeg forvente af mit wifi?"
                    }
                ]}
            />
        </>
    )
}
